import React from "react";
import vtLogo from "../../assets/vt_logo_tight.png";

/**
 * Loader — Ocean Blue Design System
 *
 * Variants:
 *   Spinner         — circular spinner ring
 *   Skeleton        — shimmer placeholder block
 *   PageLoader      — full-screen centered spinner overlay
 *   TableSkeleton   — tabular rows skeleton
 *   InlineLoader    — tiny inline spinner for buttons / cells
 *
 * Props (Spinner):
 *   size    : "xs" | "sm" | "md" | "lg" | "xl"
 *   color   : "primary" | "white" | "success" | "warning" | "danger"
 *   label   : string — accessible aria-label
 */

/* ── Spinner ────────────────────────────────────────── */
const SPINNER_SIZES = {
  xs: "w-3.5 h-3.5 border-[1.5px]",
  sm: "w-5 h-5 border-2",
  md: "w-8 h-8 border-2",
  lg: "w-12 h-12 border-[3px]",
  xl: "w-16 h-16 border-4",
};

const SPINNER_COLORS = {
  primary: "border-[#E2E8F0] border-t-[#004BB5]",
  white: "border-white/30 border-t-white",
  success: "border-[#D1FAE5] border-t-[#10B981]",
  warning: "border-[#FEF3C7] border-t-[#F59E0B]",
  danger: "border-[#FEE2E2] border-t-[#EF4444]",
};

export const Spinner = ({
  size = "md",
  className = "",
  label = "Loading…",
  ...props
}) => (
  <LogoLoader size={size} className={className} label={label} {...props} />
);

/* ── LogoLoader (Exact VT Emblem Inside Spinner Ring) ── */
const LOGO_LOADER_SIZES = {
  xs: { ring: "w-5 h-5",   logo: "w-2.5 h-2.5", stroke: 3   },
  sm: { ring: "w-8 h-8",   logo: "w-4 h-4",     stroke: 3.5 },
  md: { ring: "w-12 h-12", logo: "w-5 h-5",     stroke: 4.5 },
  lg: { ring: "w-16 h-16", logo: "w-8 h-8",     stroke: 5   },
  xl: { ring: "w-22 h-22", logo: "w-11 h-11",   stroke: 5.5 },
};

export const LogoLoader = ({
  size = "md",
  className = "",
}) => {
  const cfg = LOGO_LOADER_SIZES[size] || LOGO_LOADER_SIZES.md;

  return (
    <div className={["flex items-center justify-center p-2", className].join(" ")}>
      <div className={`relative ${cfg.ring} flex items-center justify-center shrink-0`}>
        {/* Soft background ambient glow */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500/15 to-purple-500/15 blur-sm pointer-events-none" />

        {/* Tapering Comet-Trail Dotted Spinner Ring (Big -> Mid -> Small) */}
        <svg
          className="absolute inset-0 w-full h-full animate-spin"
          viewBox="0 0 100 100"
          style={{ animationDuration: "1.15s" }}
        >
          {/* Inactive Track: Tiny Light Dots */}
          <circle cx="65.31" cy="13.04" r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="78.28" cy="21.72" r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="86.96" cy="34.69" r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="90"    cy="50"    r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="86.96" cy="65.31" r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="78.28" cy="78.28" r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="65.31" cy="86.96" r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="50"    cy="90"    r="1.5" fill="#CBD5E1" opacity="0.6" />
          <circle cx="34.69" cy="86.96" r="1.5" fill="#CBD5E1" opacity="0.6" />

          {/* Active Tapering Comet Trail: Big -> Mid -> Small (Blue to Purple) */}
          <circle cx="21.72" cy="78.28" r="1.8" fill="#A855F7" />
          <circle cx="13.04" cy="65.31" r="2.3" fill="#9333EA" />
          <circle cx="10"    cy="50"    r="2.8" fill="#7C3AED" />
          <circle cx="13.04" cy="34.69" r="3.4" fill="#6366F1" />
          <circle cx="21.72" cy="21.72" r="4.0" fill="#4F46E5" />
          <circle cx="34.69" cy="13.04" r="4.6" fill="#3B82F6" />
          {/* Head: Biggest Leading Blue Dot */}
          <circle cx="50"    cy="10"    r="5.4" fill="#2563EB" />
        </svg>

        {/* Center VT Logo (Transparent, Clear Rhythmic Blink) */}
        <div className={`${cfg.logo} z-10 flex items-center justify-center pointer-events-none vt-logo-blink`}>
          <img
            src={vtLogo}
            alt="Virtual Technologies"
            className="w-full h-full object-contain filter drop-shadow-sm select-none"
          />
        </div>
      </div>
    </div>
  );
};

/* ── PageLoader ─────────────────────────────────────── */
export const PageLoader = () => (
  <div className="fixed inset-0 z-[999] flex flex-col items-center justify-center bg-[#F8FAFC]/90 backdrop-blur-sm">
    <LogoLoader size="lg" />
  </div>
);

/* ── InlineLoader ───────────────────────────────────── */
export const InlineLoader = ({ size = "sm", color = "primary", className = "" }) => (
  <span className={["inline-flex items-center gap-2", className].join(" ")}>
    <Spinner size={size} color={color} />
  </span>
);

/* ── Skeleton ───────────────────────────────────────── */
const shimmerBase =
  "rounded-lg bg-gradient-to-r from-[#F1F5F9] via-[#E8EDF4] to-[#F1F5F9] " +
  "bg-[length:400%_100%] animate-[shimmer_2s_linear_infinite]";

export const Skeleton = ({ className = "", ...props }) => (
  <div className={[shimmerBase, className].filter(Boolean).join(" ")} {...props} />
);

Skeleton.Text = ({ lines = 3, className = "" }) => (
  <div className={["space-y-2.5", className].join(" ")}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={[
          "h-3.5",
          i === lines - 1 && lines > 1 ? "w-3/5" : "w-full",
        ].join(" ")}
      />
    ))}
  </div>
);

Skeleton.Avatar = ({ size = "md", className = "" }) => {
  const sz = { sm: "w-8 h-8", md: "w-10 h-10", lg: "w-14 h-14" }[size] ?? "w-10 h-10";
  return <Skeleton className={[sz, "rounded-full", className].join(" ")} />;
};

Skeleton.Button = ({ className = "" }) => (
  <Skeleton className={["h-9 w-24 rounded-lg", className].join(" ")} />
);

Skeleton.KpiCard = ({ className = "" }) => (
  <div className={["bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4", className].join(" ")}>
    <div className="flex items-center justify-between">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="w-10 h-10 rounded-xl" />
    </div>
    <Skeleton className="h-8 w-28" />
    <Skeleton className="h-3 w-20" />
  </div>
);

Skeleton.TableRow = ({ cols = 5, className = "" }) => (
  <div
    className={[
      "flex items-center gap-4 px-5 py-3.5 border-b border-[#F1F5F9]",
      className,
    ].join(" ")}
  >
    {Array.from({ length: cols }).map((_, i) => (
      <Skeleton key={i} className="h-3.5 flex-1" style={{ maxWidth: i === 0 ? 40 : undefined }} />
    ))}
  </div>
);

/* ── TableSkeleton ──────────────────────────────────── */
export const TableSkeleton = ({
  rows = 6,
  cols = 5,
  showHeader = true,
  className = "",
}) => (
  <div
    className={[
      "bg-white border border-[#E2E8F0] rounded-xl overflow-hidden",
      "shadow-[0_1px_3px_rgba(15,23,42,0.06)]",
      className,
    ].join(" ")}
  >
    {showHeader && (
      <div className="flex items-center gap-4 px-5 py-3.5 bg-[#F8FAFC] border-b border-[#E2E8F0]">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
    )}
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton.TableRow key={i} cols={cols} />
    ))}
  </div>
);

/* ── ProgressBar ────────────────────────────────────── */
export const ProgressBar = ({
  value,
  max = 100,
  color = "primary",
  size = "md",
  label,
  showValue = false,
  className = "",
}) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const heights = { sm: "h-1", md: "h-2", lg: "h-3" };
  const colors = {
    primary: "bg-[#004BB5]",
    success: "bg-[#10B981]",
    warning: "bg-[#F59E0B]",
    danger: "bg-[#EF4444]",
  };

  return (
    <div className={className}>
      {(label || showValue) && (
        <div className="flex justify-between items-center mb-1.5">
          {label && <span className="text-xs text-[#475569]">{label}</span>}
          {showValue && (
            <span className="text-xs font-semibold text-[#0F172A]">
              {Math.round(pct)}%
            </span>
          )}
        </div>
      )}
      <div
        className={[
          "w-full bg-[#E2E8F0] rounded-full overflow-hidden",
          heights[size] ?? heights.md,
        ].join(" ")}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div
          className={[
            "h-full rounded-full transition-all duration-500 ease-out",
            colors[color] ?? colors.primary,
          ].join(" ")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default LogoLoader;
